import type { PmsJobRecord } from '../../core/pms/_models'
import type { RiskLibraryRiskNumberOption } from '../../../QHSE/core/_models'

const CATEGORY_TO_SHORT: Record<string, string> = {
  DECK_ROUTINE: 'DR',
  DECK_NON_ROUTINE: 'DN',
  ENGINE_ROUTINE: 'ER',
  ENGINE_NON_ROUTINE: 'EN',
  CRITICAL_EQUIPMENT: 'CE',
}

export function categoryCodeToShort(categoryCode: string): string {
  const k = (categoryCode || '').toUpperCase()
  return CATEGORY_TO_SHORT[k] || '??'
}

export function formatRiskAssessmentDisplay(
  categoryCode: string,
  categoryTaskNumber: number,
  taskVersion: number
): string {
  const sh = categoryCodeToShort(categoryCode)
  const n = categoryTaskNumber >= 0 && categoryTaskNumber < 100 ? String(categoryTaskNumber).padStart(2, '0') : String(categoryTaskNumber)
  const v = taskVersion >= 0 && taskVersion <= 99 ? String(taskVersion).padStart(2, '0') : String(taskVersion)
  return `RA-${sh}-${n}.${v}`
}

/** Canonical RA number from library, e.g. RA-CE-01.00 */
export function riskNumberIsCriticalEquipment(display: string): boolean {
  const s = (display || '').trim().toUpperCase()
  return s.startsWith('RA-CE')
}

/** Matches backend PmsJobRiskAssessmentRules: hide CE for overdue plan-line jobs without defect context. */
export function shouldExcludeCriticalEquipment(job: PmsJobRecord): boolean {
  if (!isOverdueContext(job)) return false
  if (!job.planLineId) return false
  const defectContext = (job.defectId != null && job.defectId > 0) || job.jobDueReason === 'DEFECT'
  return !defectContext
}

function isOverdueContext(job: PmsJobRecord): boolean {
  if (job.state === 'OVERDUE') return true
  if (!job.dueDate) return false
  const due = new Date(job.dueDate)
  const today = new Date()
  due.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)
  if (due >= today) return false
  // OVERDUE already returned above; remaining branch is past-due jobs still open
  return job.state === 'IN_PROGRESS' || job.state === 'DEFERRED'
}

export function normalizeRiskSearchToken(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

export function riskOptionMatchesSearch(opt: RiskLibraryRiskNumberOption, rawQuery: string): boolean {
  const q = normalizeRiskSearchToken(rawQuery)
  if (!q) return true
  const hay = normalizeRiskSearchToken(
    [
      opt.displayNumber,
      opt.title,
      opt.categoryCode,
      categoryCodeToShort(opt.categoryCode),
      String(opt.categoryTaskNumber ?? ''),
      String(opt.taskVersion ?? ''),
    ].join(' ')
  )
  return hay.includes(q)
}
